import React, { useState, useRef } from 'react';
import { motion, useSpring, useMotionValue } from 'framer-motion';
import { useCopyMode } from './copy/copyMode';
const FOUNDER_PHOTO_URL = 'https://cdn.magicpatterns.com/uploads/17254cM6qXXJBjqAfHJEz5/image_14.png';
// A founder quote is built from inline "tokens" so we can mix plain runs of
// text with highlighted phrases. The founder's head no longer sits inline in
// the sentence — it only appears as the custom cursor that follows the mouse.
type QuoteToken = {
  type: 'text';
  value: string;
} | {
  type: 'mark';
  value: string;
};
// Real founder copy, tuned per audience. The `mark` tokens are the phrases
// that get the soft orange pill + orange text treatment.
const QUOTE_BY_MODE: Record<string, QuoteToken[]> = {
  // Team / AI colleague leaning.
  current: [{
    type: 'text',
    value: 'I built Giga so my team could stop '
  }, {
    type: 'mark',
    value: 'drowning in busywork'
  }, {
    type: 'text',
    value: ' and spend their hours on the work that '
  }, {
    type: 'mark',
    value: 'actually moves us forward'
  }, {
    type: 'text',
    value: '.'
  }],
  // Outcome / time-back leaning.
  outcome: [{
    type: 'text',
    value: "I'm done with my team losing whole days to "
  }, {
    type: 'mark',
    value: 'copy-pasting between tabs'
  }, {
    type: 'text',
    value: '. Giga just '
  }, {
    type: 'mark',
    value: 'gets it done while you sleep'
  }, {
    type: 'text',
    value: '.'
  }],
  // Founder / lean-team leaning — the one the user has been refining.
  founder: [{
    type: 'text',
    value: 'I run a tiny team. Giga is how we '
  }, {
    type: 'mark',
    value: 'cover finance, marketing, and ops'
  }, {
    type: 'text',
    value: ' without hiring — and '
  }, {
    type: 'mark',
    value: 'outwork companies 10x our size'
  }, {
    type: 'text',
    value: '.'
  }]
};
export function NarrativeSection() {
  const {
    mode
  } = useCopyMode();
  const tokens = QUOTE_BY_MODE[mode] ?? QUOTE_BY_MODE.current;
  const sectionRef = useRef<HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  // Mouse tracking for the custom cursor head (desktop only).
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  // Smooth spring physics so the head trails the cursor with a slight floaty,
  // "multiplayer" feel.
  const springX = useSpring(mouseX, {
    stiffness: 800,
    damping: 35,
    mass: 0.5
  });
  const springY = useSpring(mouseY, {
    stiffness: 800,
    damping: 35,
    mass: 0.5
  });
  const handleMouseMove = (e: React.MouseEvent) => {
    mouseX.set(e.clientX);
    mouseY.set(e.clientY);
  };
  // Plain-text version of the quote for screen readers + a11y label.
  const plainQuote = tokens.map(t => t.value).join('');
  return <section ref={sectionRef} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onMouseMove={handleMouseMove} aria-label={`${plainQuote} — Tony Ennis, Founder`} className="relative w-full bg-[#EA580C] text-white py-16 md:py-40 overflow-hidden md:cursor-none" data-id="element-1854">
      {/* Halftone dot matrix over the orange field, like the reference. */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-60" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='1.5' height='1.5' fill='%23000000' fill-opacity='0.10'/%3E%3C/svg%3E\")",
      backgroundSize: '14px 14px'
    }} data-id="element-1855" />

      <div className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 flex flex-col items-start md:items-center" data-id="element-1856">
        {/* Mobile-only eyebrow — a warm, personal intro to the founder quote,
         matching the reference ("HEY, IT'S TONY"). */}
        <span className="md:hidden mb-5 text-[11px] font-mono font-bold tracking-[0.35em] uppercase text-white/90" data-id="element-1857">
          Hey, it&rsquo;s Tony
        </span>

        {/* Display quote with inline highlights + inline founder head cursor. */}
        <h2 aria-hidden="true" className="text-[26px] leading-[1.5] sm:text-4xl sm:leading-[1.35] md:text-6xl md:leading-[1.22] font-title font-medium tracking-tight text-left md:text-center text-balance max-w-5xl mx-auto" data-id="element-1858">
          <span className="text-white/90" data-id="element-1859">&ldquo;</span>
          {tokens.map((token, i) => {
          if (token.type === 'mark') {
            return <span key={i} className="inline bg-[#FBDDC9] text-[#EA580C] font-medium rounded-full px-2.5 py-1 md:px-5 md:py-1 mx-0.5 md:mx-1 decoration-clone box-decoration-clone leading-tight" data-id="element-1860">
                  {token.value}
                </span>;
          }
          return <span key={i} data-id="element-1861">{token.value}</span>;
        })}
          <span className="text-white/90" data-id="element-1862">&rdquo;</span>
        </h2>

        {/* Founder attribution beneath the quote. On desktop the head trails the
         cursor, so only the centered text line shows. On mobile there's no
         cursor, so we surface Tony's head here — sitting to the left of his
         title, matching the reference layout. */}
        {/* Mobile attribution — head left, two-line title right. */}
        <div className="md:hidden mt-8 flex items-center gap-4" data-id="element-1863">
          <img src={FOUNDER_PHOTO_URL} alt="Tony Ennis, Founder & CEO of Giga" className="shrink-0 w-16 h-16 object-contain object-top drop-shadow-[0_6px_14px_rgba(0,0,0,0.3)]" data-id="element-1864" />
          <div className="text-[11px] font-mono font-bold tracking-[0.32em] uppercase leading-relaxed text-white/90" data-id="element-1865">
            Founder,
            <br data-id="element-1866" />
            Giga
          </div>
        </div>

        {/* Desktop attribution — centered single text line. */}
        <div className="hidden md:flex mt-12 flex-col items-center" data-id="element-1867">
          <div className="text-center text-xs font-mono font-bold tracking-[0.35em] uppercase" data-id="element-1868">
            <span className="text-white" data-id="element-1869">Tony Ennis</span>
            <span className="text-white/40 mx-2" data-id="element-1870">·</span>
            <span className="text-white/70" data-id="element-1871">Founder</span>
          </div>
        </div>
      </div>

      {/* Custom founder cursor — desktop only. Replaces the system cursor with
                                  Tony's head so moving the mouse over the quote feels like he's
                                  pointing at his own words. */}
      <motion.div className="hidden md:flex fixed top-0 left-0 z-50 pointer-events-none items-center justify-center" style={{
      x: springX,
      y: springY,
      opacity: isHovered ? 1 : 0,
      scale: isHovered ? 1 : 0.5
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 25
    }} data-id="element-1872">
        <div className="relative w-16 h-16 md:w-20 md:h-20 -ml-8 -mt-8 md:-ml-10 md:-mt-10" data-id="element-1873">
          <img src={FOUNDER_PHOTO_URL} alt="" aria-hidden="true" className="w-full h-full object-contain object-top drop-shadow-[0_6px_14px_rgba(0,0,0,0.3)]" data-id="element-1874" />
        </div>
      </motion.div>
    </section>;
}